import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Trash2, Plus, Copy, ChevronUp, ChevronDown, Check, CloudOff, Keyboard, AlertTriangle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { ProductCombobox } from "@/components/common/ProductCombobox";
import { CustomerCombobox, pushRecentCustomer } from "@/components/common/CustomerCombobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const MARGIN_TARGET = 15; // %
function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }
function calcLine(l) {
  const gross = round2((parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0));
  const discount = round2(gross * (parseFloat(l.discount_percent) || 0) / 100);
  const net = gross - discount;
  const tax = round2(net * (parseFloat(l.vat_rate) || 0) / 100);
  return { net, discount, tax, total: net + tax };
}
function lineMargin(l) {
  const price = parseFloat(l.unit_price) || 0;
  if (l.cost == null || price <= 0) return null;
  const cost = parseFloat(l.cost) || 0;
  const disc = parseFloat(l.discount_percent) || 0;
  const eff = price * (1 - disc / 100);
  if (eff <= 0) return null;
  return round2((eff - cost) / eff * 100);
}
const NEW_LINE = { line_type: "product", product_id: "", description: "", quantity: "1", unit: "Stk", unit_price: "0", discount_percent: "0", vat_rate: "19", is_optional: false, cost: null, price_locked: false };

const serialize = (f) => JSON.stringify({
  customer_id: f.customer_id, valid_until: f.valid_until, payment_terms_days: f.payment_terms_days,
  notes: f.notes, terms: f.terms, vehicle_id: f.vehicle_id, insurance_data: f.insurance_data,
  lines: f.line_items.map((l) => [l.line_type, l.product_id, l.description, l.quantity, l.unit, l.unit_price, l.discount_percent, l.vat_rate, l.is_optional]),
});

export default function QuoteEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t, fmtCurrency } = useI18n();
  const { modules, businessCapabilities } = useAuth();
  const automotiveEnabled = modules?.includes("automotive_workshop");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const storageKey = `mayr_quote_draft_${id || "new"}`;

  const [form, setForm] = useState({ customer_id: "", customer_label: "", vehicle_id: "", insurance_data: {}, valid_until: "", payment_terms_days: 14, currency: "EUR", notes: "", terms: "", line_items: [{ ...NEW_LINE }] });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);         // manual submit in-flight
  const [status, setStatus] = useState("idle");        // idle|saving|saved|error
  const [recover, setRecover] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [marginWarn, setMarginWarn] = useState(null);  // {lines:[...], proceed:fn}
  const savedIdRef = useRef(id || null);
  const savingSnap = useRef(null);
  const deleted = useRef(null);
  const pendingFocus = useRef(null);
  const descRefs = useRef({});
  const initialized = useRef(false);

  const { data: customers } = useQuery({ queryKey: ["customers-all"], queryFn: async () => (await api.get("/customers", { params: { page_size: 200, is_active: "true" } })).data });
  const { data: vehicleData = { items: [] } } = useQuery({ queryKey: ["quote-customer-vehicles", form.customer_id], enabled: automotiveEnabled && !!form.customer_id, queryFn: async () => (await api.get("/automotive/vehicles", { params: { customer_id: form.customer_id, page_size: 100 } })).data });
  const { data: existing, isLoading } = useQuery({ queryKey: ["quote", id], queryFn: async () => (await api.get(`/quotes/${id}`)).data, enabled: isEdit });

  const hydrate = (ex) => ({
    customer_id: ex.customer_id, customer_label: ex.customer_name || "", valid_until: ex.valid_until || "",
    vehicle_id: ex.vehicle_id || "", insurance_data: ex.insurance_data || {},
    payment_terms_days: ex.payment_terms_days, currency: ex.currency, notes: ex.notes || "", terms: ex.terms || "",
    line_items: ex.line_items.map((l) => ({
      line_type: l.line_type, product_id: l.product_id || "", description: l.description,
      quantity: l.quantity, unit: l.unit, unit_price: l.unit_price, discount_percent: l.discount_percent,
      vat_rate: l.vat_rate, is_optional: l.is_optional, image_url: l.image_url,
      price_explanation: l.price_snapshot?.explanation, cost: null, price_locked: true,
    })),
  });

  // load existing draft + offer crash-recovery
  useEffect(() => {
    if (initialized.current) return;
    if (isEdit) {
      if (!existing) return;
      if (existing.status !== "draft") { toast.error(t("quotes.not_editable")); navigate(`/quotes/${id}`); return; }
      setForm(hydrate(existing));
      savingSnap.current = null;
    }
    initialized.current = true;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const stored = JSON.parse(raw);
        if (stored?.form) {
          if (!isEdit || (existing && stored.ts && new Date(stored.ts) > new Date(existing.updated_at || 0))) {
            setRecover(stored);
          }
        }
      }
    } catch { /* ignore */ }
  }, [existing, isEdit]); // eslint-disable-line

  // persist crash backup on change
  useEffect(() => {
    if (!dirty) return;
    try { localStorage.setItem(storageKey, JSON.stringify({ form, savedId: savedIdRef.current, ts: new Date().toISOString() })); } catch { /* ignore */ }
  }, [form, dirty, storageKey]);

  // warn on unload with unsaved changes
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const buildPayload = useCallback((f) => ({
    customer_id: f.customer_id, valid_until: f.valid_until || null,
    vehicle_id: f.vehicle_id || null, insurance_data: f.insurance_data || {},
    payment_terms_days: Number(f.payment_terms_days) || 0, currency: f.currency, notes: f.notes, terms: f.terms,
    line_items: f.line_items.map((l) => ({
      line_type: l.line_type, product_id: l.product_id || null, description: l.description || "-",
      quantity: String(l.quantity || "0"), unit: l.unit, unit_price: String(l.unit_price || "0"),
      discount_percent: String(l.discount_percent || "0"), vat_rate: String(l.vat_rate || "0"), is_optional: l.is_optional,
    })),
  }), []);

  const persist = useCallback(async (f) => {
    const payload = buildPayload(f);
    if (savedIdRef.current) { await api.put(`/quotes/${savedIdRef.current}`, payload); return savedIdRef.current; }
    const res = await api.post("/quotes", payload);
    savedIdRef.current = res.data.id;
    return res.data.id;
  }, [buildPayload]);

  // autosave (drafts only)
  useEffect(() => {
    if (!dirty || saving) return;
    if (!form.customer_id) return;
    if (!form.line_items.some((l) => (l.description || "").trim() || l.product_id)) return;
    const snap = serialize(form);
    const timer = setTimeout(async () => {
      setStatus("saving"); savingSnap.current = snap;
      try {
        await persist(form);
        qc.invalidateQueries({ queryKey: ["quotes"] });
        if (serialize(form) === savingSnap.current) setDirty(false);
        setStatus("saved");
      } catch { setStatus("error"); }
    }, 1500);
    return () => clearTimeout(timer);
  }, [form, dirty, saving, persist, qc]);

  // focus new line after add
  useEffect(() => {
    if (pendingFocus.current != null) {
      const el = descRefs.current[pendingFocus.current];
      if (el) el.focus();
      pendingFocus.current = null;
    }
  });

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };
  const setLine = (i, k, v) => set("line_items", form.line_items.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const editPrice = (i, v) => set("line_items", form.line_items.map((l, idx) => (idx === i ? { ...l, unit_price: v, price_locked: true } : l)));

  const selectProduct = (i, p) => set("line_items", form.line_items.map((l, idx) => (
    idx === i ? { ...l, product_id: p.id, description: p.title, unit_price: (p.effective_price || p.selling_price || "0"), unit: p.unit, image_url: p.image_url, price_explanation: p.price_explanation, cost: p.cost_price != null ? p.cost_price : null, price_locked: false } : l
  )));

  // refresh price when quantity changes (respect manual overrides)
  const refreshPrice = useCallback(async (i, line) => {
    if (!line.product_id || line.price_locked || !form.customer_id) return;
    try {
      const { data } = await api.get("/pricing/preview", { params: { customer_id: form.customer_id, product_id: line.product_id, quantity: line.quantity || "1" } });
      setForm((f) => ({ ...f, line_items: f.line_items.map((l, idx) => (idx === i && l.product_id === line.product_id ? { ...l, unit_price: data.unit_price, price_explanation: data.explanation } : l)) }));
    } catch { /* ignore */ }
  }, [form.customer_id]);

  const onQty = (i, v) => {
    setLine(i, "quantity", v);
    const line = { ...form.line_items[i], quantity: v };
    clearTimeout(descRefs.current[`qt-${i}`]);
    descRefs.current[`qt-${i}`] = setTimeout(() => refreshPrice(i, line), 500);
  };

  const addLine = () => { pendingFocus.current = form.line_items.length; set("line_items", [...form.line_items, { ...NEW_LINE }]); };
  const duplicateLine = (i) => { const c = { ...form.line_items[i], price_locked: true }; const arr = [...form.line_items]; arr.splice(i + 1, 0, c); set("line_items", arr); };
  const moveLine = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= form.line_items.length) return;
    const arr = [...form.line_items]; [arr[i], arr[j]] = [arr[j], arr[i]]; set("line_items", arr);
  };
  const removeLine = (i) => {
    deleted.current = { line: form.line_items[i], index: i };
    set("line_items", form.line_items.filter((_, idx) => idx !== i));
    toast(t("quotes.line_deleted"), { action: { label: t("quotes.undo"), onClick: () => {
      const d = deleted.current; if (!d) return;
      setForm((f) => { const arr = [...f.line_items]; arr.splice(d.index, 0, d.line); return { ...f, line_items: arr }; });
      setDirty(true); deleted.current = null;
    } } });
  };

  const totals = form.line_items.reduce((acc, l) => {
    if (l.is_optional) return acc;
    const c = calcLine(l);
    return { subtotal: acc.subtotal + c.net, discount: acc.discount + c.discount, tax: acc.tax + c.tax };
  }, { subtotal: 0, discount: 0, tax: 0 });

  const doSave = async () => {
    setSaving(true);
    try {
      const savedId = await persist(form);
      setDirty(false); setStatus("saved");
      try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
      qc.invalidateQueries({ queryKey: ["quotes"] });
      toast.success(isEdit || id ? t("quotes.updated_success") : t("quotes.created_success"));
      navigate(`/quotes/${savedId}`);
    } catch (err) { toast.error(apiErrorMessage(err, t)); setStatus("error"); }
    finally { setSaving(false); }
  };

  const submit = (e) => {
    if (e) e.preventDefault();
    if (!form.customer_id) { toast.error(t("validation.required")); return; }
    if (form.line_items.length === 0) { toast.error(t("quotes.no_lines")); return; }
    const low = form.line_items.filter((l) => !l.is_optional).map((l, i) => ({ i, m: lineMargin(l), desc: l.description })).filter((x) => x.m != null && x.m < MARGIN_TARGET);
    if (low.length > 0) { setMarginWarn({ lines: low }); return; }
    doSave();
  };

  // keyboard shortcuts
  useEffect(() => {
    const h = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); submit(); }
      else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); addLine(); }
      else if (e.key === "?" && !typing) { e.preventDefault(); setShowShortcuts(true); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }); // eslint-disable-line

  const doRestore = () => { setForm(recover.form); savedIdRef.current = recover.savedId || savedIdRef.current; setDirty(true); setRecover(null); };
  const doDiscard = () => { try { localStorage.removeItem(storageKey); } catch { /* ignore */ } setRecover(null); };

  if (isEdit && isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div>
      <PageHeader
        title={isEdit ? t("quotes.edit") : t("quotes.new")}
        actions={
          <div className="flex items-center gap-2">
            <SaveStatus status={status} dirty={dirty} t={t} />
            <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9" onClick={() => setShowShortcuts(true)} data-testid="quote-shortcuts-button" title={t("quotes.shortcuts")}><Keyboard className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={() => navigate("/quotes")} data-testid="quote-back"><ArrowLeft className="h-4 w-4" />{t("common.back")}</Button>
          </div>
        }
      />

      {recover && (
        <div className="mb-4 flex flex-col items-start justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center dark:border-amber-900/40 dark:bg-amber-950/20" data-testid="quote-restore-banner">
          <div>
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">{t("quotes.recover_title")}</p>
            <p className="text-xs text-amber-700 dark:text-amber-400">{t("quotes.recover_desc")}</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={doRestore} data-testid="quote-restore-yes">{t("quotes.restore")}</Button>
            <Button size="sm" variant="ghost" onClick={doDiscard} data-testid="quote-restore-no">{t("quotes.discard")}</Button>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        <Card className="p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>{t("quotes.customer")} *</Label>
              <CustomerCombobox
                value={form.customer_id}
                label={form.customer_label || (customers?.items.find((c) => c.id === form.customer_id)?.name)}
                onSelect={(c) => { setForm((f) => ({ ...f, customer_id: c.id, customer_label: c.title, vehicle_id: f.customer_id === c.id ? f.vehicle_id : "" })); setDirty(true); form.line_items.forEach((l, i) => { if (l.product_id && !l.price_locked) refreshPrice(i, { ...l, product_id: l.product_id }); }); }}
                testId="quote-customer-combobox"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("quotes.valid_until")}</Label>
              <Input type="date" value={form.valid_until} onChange={(e) => set("valid_until", e.target.value)} data-testid="quote-valid-until" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("customers.payment_terms")}</Label>
              <Input type="number" min="0" value={form.payment_terms_days} onChange={(e) => set("payment_terms_days", e.target.value)} />
            </div>
            {automotiveEnabled && form.customer_id && <div className="space-y-1.5 md:col-span-2"><Label>{t("automotive.vehicle")}</Label><Select value={form.vehicle_id || "none"} onValueChange={(v) => set("vehicle_id", v === "none" ? "" : v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">{t("automotive.no_vehicle_general_quote")}</SelectItem>{vehicleData.items.map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_plate} · {v.make} {v.model} · {v.vin || "ohne FIN"}</SelectItem>)}</SelectContent></Select></div>}
            {automotiveEnabled && businessCapabilities?.insurance_claims !== false && form.vehicle_id && <>
              <div className="space-y-1.5"><Label>{t("automotive.insurer")}</Label><Input value={form.insurance_data?.insurer || ""} onChange={(e) => set("insurance_data", { ...form.insurance_data, insurer: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("automotive.claim_number")}</Label><Input value={form.insurance_data?.claim_number || ""} onChange={(e) => set("insurance_data", { ...form.insurance_data, claim_number: e.target.value })} /></div>
            </>}
          </div>
        </Card>

        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("quotes.line_items")}</h3>
            <Button type="button" variant="outline" size="sm" className="h-11 md:h-9" onClick={addLine} data-testid="add-line-button"><Plus className="h-4 w-4" />{t("quotes.add_line")}</Button>
          </div>
          <div className="space-y-3">
            {form.line_items.map((l, i) => {
              const c = calcLine(l);
              const m = lineMargin(l);
              return (
                <div key={i} className="rounded-md border border-slate-200 p-3 dark:border-slate-800" data-testid={`quote-line-${i}`}>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
                    <div className="md:col-span-3">
                      <Label className="text-xs">{t("quotes.line_type")}</Label>
                      <Select value={l.line_type} onValueChange={(v) => setLine(i, "line_type", v)}>
                        <SelectTrigger data-testid={`line-type-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="product">{t("products.product")}</SelectItem>
                          <SelectItem value="service">{t("products.service")}</SelectItem>
                          <SelectItem value="free_text">{t("quotes.free_text")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {l.line_type !== "free_text" && (
                      <div className="md:col-span-4">
                        <Label className="text-xs">{t("quotes.select_product")}</Label>
                        <ProductCombobox lineType={l.line_type} value={l.product_id} label={l.product_id ? l.description : ""} imageUrl={l.image_url} customerId={form.customer_id} testId={`line-product-${i}`} onSelect={(p) => selectProduct(i, p)} />
                        {l.price_explanation && l.price_explanation !== "List price" && (
                          <p className="mt-1 text-xs text-emerald-600" data-testid={`line-price-explanation-${i}`}>{l.price_explanation}</p>
                        )}
                      </div>
                    )}
                    <div className={l.line_type === "free_text" ? "md:col-span-9" : "md:col-span-5"}>
                      <Label className="text-xs">{t("quotes.description")}</Label>
                      <Input value={l.description} ref={(el) => { descRefs.current[i] = el; }} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`line-desc-${i}`} />
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-12">
                    <div className="md:col-span-2"><Label className="text-xs">{t("quotes.quantity")}</Label><Input type="number" step="0.01" className="tabular-nums" value={l.quantity} onChange={(e) => onQty(i, e.target.value)} data-testid={`line-qty-${i}`} /></div>
                    <div className="md:col-span-1"><Label className="text-xs">{t("products.unit")}</Label><Input value={l.unit} onChange={(e) => setLine(i, "unit", e.target.value)} /></div>
                    <div className="md:col-span-2"><Label className="text-xs">{t("quotes.unit_price")}</Label><Input type="number" step="0.01" className="tabular-nums" value={l.unit_price} onChange={(e) => editPrice(i, e.target.value)} data-testid={`line-price-${i}`} /></div>
                    <div className="md:col-span-2"><Label className="text-xs">{t("quotes.discount")}</Label><Input type="number" step="0.01" className="tabular-nums" value={l.discount_percent} onChange={(e) => setLine(i, "discount_percent", e.target.value)} data-testid={`line-disc-${i}`} /></div>
                    <div className="md:col-span-2"><Label className="text-xs">{t("quotes.vat")}</Label><Input type="number" step="0.01" className="tabular-nums" value={l.vat_rate} onChange={(e) => setLine(i, "vat_rate", e.target.value)} data-testid={`line-vat-${i}`} /></div>
                    <div className="col-span-2 flex flex-wrap items-end justify-end gap-1 md:col-span-3">
                      <div className="mr-auto text-right">
                        <Label className="text-xs">{t("quotes.line_net")}</Label>
                        <div className="flex h-9 items-center justify-end text-sm font-medium tabular-nums" data-testid={`line-net-${i}`}>{fmtCurrency(c.net)}</div>
                      </div>
                      <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 md:h-8 md:w-8" disabled={i === 0} onClick={() => moveLine(i, -1)} data-testid={`line-up-${i}`} title={t("quotes.move_up")}><ChevronUp className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 md:h-8 md:w-8" disabled={i === form.line_items.length - 1} onClick={() => moveLine(i, 1)} data-testid={`line-down-${i}`} title={t("quotes.move_down")}><ChevronDown className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 md:h-8 md:w-8" onClick={() => duplicateLine(i)} data-testid={`line-dup-${i}`} title={t("quotes.duplicate_line")}><Copy className="h-4 w-4" /></Button>
                      <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 md:h-8 md:w-8" onClick={() => removeLine(i)} data-testid={`remove-line-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <Checkbox id={`opt-${i}`} checked={l.is_optional} onCheckedChange={(v) => setLine(i, "is_optional", Boolean(v))} data-testid={`line-optional-${i}`} />
                    <Label htmlFor={`opt-${i}`} className="text-xs text-slate-500">{t("quotes.is_optional")}</Label>
                    {m != null && (
                      <span className={`ml-auto rounded px-2 py-0.5 text-xs font-medium tabular-nums ${m < MARGIN_TARGET ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"}`} data-testid={`line-margin-${i}`}>
                        {t("quotes.margin")}: {m}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">{t("quotes.subtotal")}</span><span className="tabular-nums" data-testid="preview-subtotal">{fmtCurrency(round2(totals.subtotal))}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("quotes.discount_total")}</span><span className="tabular-nums">{fmtCurrency(round2(totals.discount))}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">{t("quotes.tax_total")}</span><span className="tabular-nums">{fmtCurrency(round2(totals.tax))}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1 font-heading text-base font-bold dark:border-slate-700"><span>{t("quotes.total")}</span><span className="tabular-nums" data-testid="preview-total">{fmtCurrency(round2(totals.subtotal + totals.tax))}</span></div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-1.5"><Label>{t("common.notes")}</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} /></div>
          <div className="space-y-1.5"><Label>{t("quotes.terms")}</Label><Textarea value={form.terms} onChange={(e) => set("terms", e.target.value)} rows={3} /></div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/quotes")}>{t("common.cancel")}</Button>
          <Button type="submit" disabled={saving} className="h-11 md:h-9" data-testid="quote-save-button">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("common.save")}</Button>
        </div>
      </form>

      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent data-testid="quote-shortcuts-dialog">
          <DialogHeader><DialogTitle>{t("quotes.shortcuts_title")}</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <Row k="⌘/Ctrl + S" v={t("quotes.sc_save")} />
            <Row k="⌘/Ctrl + ⏎" v={t("quotes.sc_add_line")} />
            <Row k="?" v={t("quotes.sc_help")} />
            <Row k="Esc" v={t("quotes.sc_close")} />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!marginWarn} onOpenChange={(o) => !o && setMarginWarn(null)}>
        <DialogContent data-testid="quote-margin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" />{t("quotes.margin_warning_title")}</DialogTitle>
            <DialogDescription>{t("quotes.margin_warning_desc", { pct: MARGIN_TARGET })}</DialogDescription>
          </DialogHeader>
          <div className="max-h-48 space-y-1 overflow-auto text-sm">
            {marginWarn?.lines.map((x) => (
              <div key={x.i} className="flex justify-between rounded bg-slate-50 px-3 py-1.5 dark:bg-slate-800/50">
                <span className="truncate">{x.desc || `#${x.i + 1}`}</span>
                <span className="tabular-nums font-medium text-red-600">{x.m}%</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarginWarn(null)} data-testid="margin-review">{t("quotes.review")}</Button>
            <Button onClick={() => { setMarginWarn(null); doSave(); }} data-testid="margin-save-anyway">{t("quotes.save_anyway")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-600 dark:text-slate-300">{v}</span>
    <kbd className="rounded border border-slate-300 bg-slate-100 px-2 py-0.5 font-mono text-xs dark:border-slate-700 dark:bg-slate-800">{k}</kbd>
  </div>
);

function SaveStatus({ status, dirty, t }) {
  const base = { "aria-live": "polite" };
  if (status === "saving") return <span {...base} className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="quote-save-status"><Loader2 className="h-3.5 w-3.5 animate-spin" />{t("quotes.saving")}</span>;
  if (status === "error") return <span {...base} className="flex items-center gap-1.5 text-xs text-red-600" data-testid="quote-save-status"><CloudOff className="h-3.5 w-3.5" />{t("quotes.save_failed")}</span>;
  if (status === "saved" && !dirty) return <span {...base} className="flex items-center gap-1.5 text-xs text-emerald-600" data-testid="quote-save-status"><Check className="h-3.5 w-3.5" />{t("quotes.saved")}</span>;
  return <span {...base} className="text-xs text-slate-400" data-testid="quote-save-status">{dirty ? "•" : ""}</span>;
}
